import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import {
  getResourceCatalogue,
  createResourceCatalogueItem,
  updateResourceCatalogueItem,
  deleteResourceCatalogueItem,
} from '../api/resourceCatalogue';
import { ResourceCatalogueItem } from '../types/quote';

function sortByDescription(items: ResourceCatalogueItem[]): ResourceCatalogueItem[] {
  return [...items].sort((a, b) => a.description.localeCompare(b.description));
}

const ResourceCataloguePage: React.FC = () => {
  const [items, setItems] = useState<ResourceCatalogueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newDescription, setNewDescription] = useState('');
  const [newRate, setNewRate] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editRate, setEditRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    getResourceCatalogue()
      .then(setItems)
      .catch(() => setError('Failed to load your Resource Catalogue.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const description = newDescription.trim();
    const rate = parseFloat(newRate);
    if (!description || Number.isNaN(rate) || rate < 0) return;
    setAdding(true);
    setAddError(null);
    try {
      const created = await createResourceCatalogueItem(description, rate);
      setItems(prev => sortByDescription([...prev, created]));
      setNewDescription('');
      setNewRate('');
    } catch {
      setAddError('Failed to add entry.');
    } finally {
      setAdding(false);
    }
  }

  function startEdit(item: ResourceCatalogueItem) {
    setEditingId(item.id);
    setEditDescription(item.description);
    setEditRate(String(item.ratePerHour));
    setSaveError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setSaveError(null);
  }

  async function handleSaveEdit(id: number) {
    const description = editDescription.trim();
    const rate = parseFloat(editRate);
    if (!description || Number.isNaN(rate) || rate < 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateResourceCatalogueItem(id, description, rate);
      setItems(prev => sortByDescription(prev.map(item => (item.id === id ? updated : item))));
      setEditingId(null);
    } catch {
      setSaveError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    setDeletingId(id);
    try {
      await deleteResourceCatalogueItem(id);
      setItems(prev => prev.filter(item => item.id !== id));
    } catch {
      setError('Failed to delete entry.');
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-surface-container-lowest font-sans antialiased">
      <TopAppBar />
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/contractor-profile" className="text-primary hover:underline text-sm">Contractor Profile</Link>
          <span className="text-outline">/</span>
          <h1 className="text-3xl font-bold text-on-surface">Resource Catalogue</h1>
        </div>
        <p className="text-sm text-outline mb-6">
          Save your standard labour rates here so you can pick them straight into a Quote instead of typing them out each time.
        </p>

        {error && <p className="text-error mb-4">{error}</p>}

        {/* Add entry */}
        <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant p-6 mb-6">
          <h2 className="text-base font-semibold text-on-surface mb-4">Add entry</h2>
          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              placeholder="Description, e.g. Labourer"
              className="w-full sm:flex-1 border border-outline-variant rounded-lg px-3 py-2 text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={adding}
            />
            <input
              type="number"
              min="0"
              step="0.01"
              value={newRate}
              onChange={e => setNewRate(e.target.value)}
              placeholder="Rate/hour"
              className="w-full sm:w-32 border border-outline-variant rounded-lg px-3 py-2 text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={adding}
            />
            <button
              type="submit"
              disabled={adding || !newDescription.trim() || !newRate}
              className="w-full sm:w-auto px-4 py-2 bg-primary text-on-primary rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {adding ? 'Adding...' : 'Add'}
            </button>
          </form>
          {addError && <p className="text-error text-sm mt-2">{addError}</p>}
        </div>

        {/* Entry list */}
        <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant p-6">
          <p className="text-on-surface-variant text-sm mb-4">{items.length} entries</p>
          <ul className="divide-y divide-outline-variant">
            {items.map(item => (
              <li key={item.id} className="py-3">
                {editingId === item.id ? (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <input
                      type="text"
                      value={editDescription}
                      onChange={e => setEditDescription(e.target.value)}
                      className="w-full sm:flex-1 border border-outline-variant rounded-lg px-3 py-2 text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
                      disabled={saving}
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editRate}
                      onChange={e => setEditRate(e.target.value)}
                      className="w-full sm:w-32 border border-outline-variant rounded-lg px-3 py-2 text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
                      disabled={saving}
                    />
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(item.id)}
                        disabled={saving || !editDescription.trim() || !editRate}
                        className="text-primary text-sm font-medium hover:underline disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" onClick={cancelEdit} disabled={saving} className="text-outline text-sm hover:underline disabled:opacity-50">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-on-surface">{item.description}</span>
                      <span className="text-sm text-outline ml-2">R{item.ratePerHour}/hr</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <button onClick={() => startEdit(item)} className="text-primary text-sm hover:underline">
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={deletingId === item.id}
                        className="text-error text-sm hover:underline disabled:opacity-50"
                      >
                        {deletingId === item.id ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  </div>
                )}
                {editingId === item.id && saveError && <p className="text-error text-sm mt-2">{saveError}</p>}
              </li>
            ))}
            {items.length === 0 && (
              <li className="py-4 text-center text-on-surface-variant text-sm">No entries yet.</li>
            )}
          </ul>
        </div>
      </main>
      <BottomNavBar />
    </div>
  );
};

export default ResourceCataloguePage;
